import { useCallback, useEffect, useRef, useState } from 'preact/hooks';

interface Entry {
  id: string;
  /** The trigger button. Its PARENT is the popover's wrap, which also holds the
   *  panel — that is how nesting is detected without threading panel refs. */
  trigger: HTMLElement | null;
}

/** The open popovers, outermost first. Usually length 0 or 1; length 2 when the
 *  site switcher is opened from inside the compact "⋯" menu. */
let stack: Entry[] = [];
let seq = 0;
const subscribers = new Set<() => void>();

function publish(next: Entry[]): void {
  stack = next;
  // Copy first: a subscriber that unmounts on close mutates the set mid-loop.
  for (const notify of [...subscribers]) notify();
}

/** Is `el` rendered inside `entry`'s popover (trigger or panel)? */
function contains(entry: Entry, el: HTMLElement | null): boolean {
  const wrap = entry.trigger?.parentElement;
  return !!wrap && !!el && wrap.contains(el);
}

export interface ChromePopover {
  open: boolean;
  /** Open this one, closing whatever else was open that is not its ancestor. */
  toggle: () => void;
  close: () => void;
  /** Bind to the TRIGGER's onClick. Stops the window listener from seeing the
   *  same click and closing what this press just opened. */
  onTriggerClick: (e: { stopPropagation: () => void }) => void;
}

/**
 * Mutual exclusion for the app chrome's dropdowns — bell, site switcher,
 * language, account and the compact "⋯" menu are ONE at a time.
 *
 * ⚠ Why this exists. Each of those used to own a `useState` plus its own
 * close-on-window-click effect, and every trigger calls `stopPropagation()` so
 * that its own press does not immediately close it. That combination is the bug
 * the user reported (2026-09-16): pressing the site switcher while the bell was
 * open stopped the click before the bell's window listener ever ran, so BOTH
 * panels stayed on screen. No amount of per-component fixing solves it, because
 * no component can see the others — the open popover has to be single state.
 *
 * Module-level rather than a context: `SiteSwitcher` and `NotificationBell`
 * reach the bar through slots (`actions`, `bell`) supplied by the app layer, and
 * the "⋯" menu renders the SAME `actions` node a second time. A provider would
 * have to wrap every slot at every call site to cover them; a store covers them
 * by existing. Exactly one app chrome is mounted at a time, which is what makes
 * a singleton honest here.
 *
 * A STACK rather than one id because the compact "⋯" menu contains the site
 * switcher: opening a child must not close the parent that renders it, or the
 * child unmounts mid-click and nothing opens at all. Ancestry is read off the
 * DOM at press time, so no component has to declare its nesting.
 *
 * Also folds in close-on-outside-click and Esc (focus returns to `triggerRef`),
 * which were three near-identical copies before.
 */
export function useChromePopover(triggerRef?: { current: HTMLElement | null }): ChromePopover {
  const idRef = useRef<string | null>(null);
  idRef.current ??= `chrome-popover-${++seq}`;
  const id = idRef.current;
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const notify = () => setOpen(stack.some((e) => e.id === id));
    subscribers.add(notify);
    notify();
    return () => {
      subscribers.delete(notify);
      // Unmounted while open (a route change, or a control that folds away at a
      // narrower width) must not leave the stack pointing at a dead entry.
      if (stack.some((e) => e.id === id)) stack = stack.filter((e) => e.id !== id);
    };
  }, [id]);

  /** Close this popover AND anything opened from inside it. */
  const close = useCallback(() => {
    const at = stack.findIndex((e) => e.id === id);
    if (at === -1) return;
    publish(stack.slice(0, at));
  }, [id]);

  const toggle = useCallback(() => {
    if (stack.some((e) => e.id === id)) {
      close();
      return;
    }
    const trigger = triggerRef?.current ?? null;
    // Keep only the popovers this one is rendered inside; close the siblings.
    const ancestors = stack.filter((e) => contains(e, trigger));
    publish([...ancestors, { id, trigger }]);
  }, [id, close, triggerRef]);

  const onTriggerClick = useCallback(
    (e: { stopPropagation: () => void }) => {
      e.stopPropagation();
      toggle();
    },
    [toggle],
  );

  useEffect(() => {
    if (!open) return;
    const onClick = () => close();
    const onKey = (e: KeyboardEvent) => {
      // Esc closes the INNERMOST popover only, so it backs out one level at a
      // time instead of dismissing the menu a nested list was opened from.
      if (e.key !== 'Escape' || stack[stack.length - 1]?.id !== id) return;
      close();
      triggerRef?.current?.focus();
    };
    window.addEventListener('click', onClick);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('click', onClick);
      window.removeEventListener('keydown', onKey);
    };
  }, [open, close, triggerRef]);

  return { open, toggle, close, onTriggerClick };
}
